/**
 * Payments abstraction.
 * Picks a provider based on the runtime:
 *   - Native Android (Capacitor + Play Store)  → Google Play Billing
 *   - Web (Chrome, mobile Safari, PWA)          → Stripe Checkout
 *
 * Both provider functions have the same signature:
 *   buy(product) -> Promise<{ ok: true } | never>
 * On success the caller should:
 *   - refresh the "owned" set
 *   - stay on /market (Stripe path redirects to Stripe and comes back with ?session_id)
 *
 * Google Play policy: any digital-content purchase inside an Android app on the
 * Play Store MUST go through Play Billing. We hard-block Stripe on native.
 */
import { api } from "@/lib/api";

export function isNativeAndroid() {
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  if (!cap?.isNativePlatform) return false;
  try {
    return cap.isNativePlatform() && cap.getPlatform?.() === "android";
  } catch {
    return false;
  }
}

/** Web / PWA path — Stripe Checkout. Redirects; never returns on success. */
async function stripeCheckoutFor(product) {
  const r = await api.post(`/products/${product.product_id}/checkout`, {
    origin_url: window.location.origin,
  });
  if (!r.data?.url) throw new Error("No checkout url");
  window.location.href = r.data.url;
  return { ok: true };
}

/**
 * Android / Play Store path — Google Play Billing via cordova-plugin-purchase.
 * Requires the plugin to be initialised once on app boot (see initPlayBilling()).
 */
async function playBillingFor(product) {
  const store = window?.CdvPurchase?.store;
  if (!store) {
    throw new Error(
      "In-app purchases are not available. Update the app or restart it."
    );
  }
  if (!product.play_product_id) {
    throw new Error("This item is not available on Android yet.");
  }

  const CdvPurchase = window.CdvPurchase;
  const offer = store.get(product.play_product_id)?.getOffer();
  if (!offer) {
    throw new Error(`SKU '${product.play_product_id}' not found in Play Console.`);
  }

  // Wait for the transaction to update to APPROVED, then verify with our backend.
  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      store.off(onApproved);
      store.off(onFinished);
      store.off(onError);
    };

    const onApproved = async (transaction) => {
      if (settled) return;
      try {
        const token = transaction?.nativePurchase?.purchaseToken || transaction?.transactionId;
        if (!token) throw new Error("Missing purchase token");
        // Server-side verify + acknowledge
        await api.post(`/products/${product.product_id}/play/verify`, {
          purchase_token: token,
          product_id: product.play_product_id,
        });
        // Tell the store to consume/finish so BillingClient marks it fulfilled.
        await transaction.finish();
      } catch (err) {
        if (!settled) {
          settled = true;
          cleanup();
          reject(err);
        }
      }
    };

    const onFinished = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ ok: true });
    };

    const onError = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(err?.message || "Play Billing error"));
    };

    store.when()
      .approved(onApproved)
      .finished(onFinished);
    store.error(onError);

    // Kick off the purchase flow
    offer.order().catch(onError);
  });
}

export const payments = {
  provider: isNativeAndroid() ? "play" : "stripe",
  buy(product) {
    return isNativeAndroid() ? playBillingFor(product) : stripeCheckoutFor(product);
  },
};

/**
 * Call once at app boot on native Android to register SKUs with the store.
 * Safe to call multiple times.
 */
export async function initPlayBilling() {
  if (!isNativeAndroid() || !window.CdvPurchase) return;
  const { store, Platform, ProductType, LogLevel } = window.CdvPurchase;

  try {
    // Fetch the SKU list from our backend so the client and server agree.
    const r = await api.get("/play/products");
    const skus = (r.data || [])
      .map((p) => p.play_product_id)
      .filter(Boolean);
    if (skus.length === 0) return;

    store.verbosity = LogLevel.WARNING;
    store.register(
      skus.map((id) => ({ id, type: ProductType.CONSUMABLE, platform: Platform.GOOGLE_PLAY }))
    );
    await store.initialize([Platform.GOOGLE_PLAY]);
  } catch (err) {
    console.warn("Play Billing init failed:", err);
  }
}
